import { db } from './firebase';
import { collection, query, where, getDocs, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

/**
 * Discount calculation and validation utilities
 */

// Discount types
export const DISCOUNT_TYPES = {
  PERCENTAGE: 'percentage',
  FIXED: 'fixed',
  FREE_SHIPPING: 'free_shipping'
};

/**
 * Calculate discount amount based on type and value
 * @param {string} type - Discount type (percentage, fixed, free_shipping)
 * @param {number} value - Discount value (percentage or fixed amount)
 * @param {number} subtotal - Order subtotal
 * @param {number} deliveryFee - Delivery fee
 * @param {Object} settings - Discount settings
 * @returns {Object} - { discountAmount, discountType, finalAmount }
 */
export function calculateDiscount(type, value, subtotal, deliveryFee = 0, settings = {}) {
  let discountAmount = 0;
  let discountType = type;
  let finalAmount = subtotal + deliveryFee;

  switch (type) {
    case DISCOUNT_TYPES.PERCENTAGE:
      discountAmount = (subtotal * value) / 100;
      // Apply max discount limit if set
      if (settings.maxDiscount && discountAmount > settings.maxDiscount) {
        discountAmount = settings.maxDiscount;
      }
      finalAmount = subtotal - discountAmount + deliveryFee;
      break;

    case DISCOUNT_TYPES.FIXED:
      discountAmount = Math.min(value, subtotal); // Can't discount more than subtotal
      finalAmount = subtotal - discountAmount + deliveryFee;
      break;

    case DISCOUNT_TYPES.FREE_SHIPPING:
      discountAmount = deliveryFee;
      discountType = 'free_shipping';
      finalAmount = subtotal; // No delivery fee
      break;

    default:
      return { discountAmount: 0, discountType: 'none', finalAmount: subtotal + deliveryFee };
  }

  return {
    discountAmount: Math.round(discountAmount * 100) / 100,
    discountType,
    finalAmount: Math.round(finalAmount * 100) / 100
  };
}

/**
 * Validate voucher code
 * @param {string} code - Voucher code
 * @param {string} userId - User ID
 * @param {number} subtotal - Order subtotal
 * @returns {Promise<Object>} - Validation result
 */
export async function validateVoucher(code, userId, subtotal) {
  try {
    // Get voucher by code
    const voucherQuery = query(
      collection(db, 'vouchers'),
      where('code', '==', code.toUpperCase()),
      where('enabled', '==', true)
    );
    
    const voucherSnap = await getDocs(voucherQuery);
    if (voucherSnap.empty) {
      return { valid: false, error: 'Invalid voucher code' };
    }

    const voucher = voucherSnap.docs[0].data();
    const voucherId = voucherSnap.docs[0].id;

    // Check if voucher is expired
    const now = new Date();
    const validFrom = voucher.validFrom.toDate();
    const validUntil = voucher.validUntil.toDate();
    
    if (now < validFrom || now > validUntil) {
      return { valid: false, error: 'Voucher is expired or not yet valid' };
    }

    // Check minimum order amount
    if (subtotal < voucher.minOrderAmount) {
      return { 
        valid: false, 
        error: `Minimum order amount is ₱${voucher.minOrderAmount}` 
      };
    }

    // Check usage limit
    if (voucher.usedCount >= voucher.usageLimit) {
      return { valid: false, error: 'Voucher usage limit reached' };
    }

    // Check if user has already used this voucher
    const userVoucherRef = doc(db, 'userVouchers', userId);
    const userVoucherSnap = await getDoc(userVoucherRef);
    
    if (userVoucherSnap.exists()) {
      const userData = userVoucherSnap.data();
      if (userData && userData.vouchers && userData.vouchers[voucherId]) {
        const usageCount = userData.vouchers[voucherId].usageCount || 0;
        if (usageCount >= voucher.usageLimit) {
          return { valid: false, error: 'You have already used this voucher' };
        }
      }
    }

    // Calculate discount
    const discount = calculateDiscount(
      voucher.type,
      voucher.value,
      subtotal,
      0, // deliveryFee will be calculated separately
      { maxDiscount: voucher.maxDiscount }
    );

    return {
      valid: true,
      voucher: {
        id: voucherId,
        code: voucher.code,
        type: voucher.type,
        value: voucher.value,
        description: voucher.description,
        minOrderAmount: voucher.minOrderAmount,
        maxDiscount: voucher.maxDiscount
      },
      discount: discount.discountAmount,
      discountType: discount.discountType,
      finalAmount: discount.finalAmount
    };

  } catch (error) {
    console.error('Error validating voucher:', error);
    return { valid: false, error: 'Error validating voucher' };
  }
}

/**
 * Validate coupon code
 * @param {string} code - Coupon code
 * @param {string} userId - User ID
 * @param {number} subtotal - Order subtotal
 * @returns {Promise<Object>} - Validation result
 */
export async function validateCoupon(code, userId, subtotal) {
  try {
    // Get coupon by code
    const couponQuery = query(
      collection(db, 'coupons'),
      where('code', '==', code.toUpperCase()),
      where('enabled', '==', true)
    );
    
    const couponSnap = await getDocs(couponQuery);
    if (couponSnap.empty) {
      return { valid: false, error: 'Invalid coupon code' };
    }

    const coupon = couponSnap.docs[0].data();
    const couponId = couponSnap.docs[0].id;

    // Check if coupon is expired
    const now = new Date();
    const validFrom = coupon.validFrom.toDate();
    const validUntil = coupon.validUntil.toDate();
    
    if (now < validFrom || now > validUntil) {
      return { valid: false, error: 'Coupon is expired or not yet valid' };
    }

    // Check minimum order amount
    if (subtotal < coupon.minOrderAmount) {
      return { 
        valid: false, 
        error: `Minimum order amount is ₱${coupon.minOrderAmount}` 
      };
    }

    // Check usage limit
    if (coupon.usedCount >= coupon.usageLimit) {
      return { valid: false, error: 'Coupon usage limit reached' };
    }

    // Check if user has already used this coupon
    const userCouponRef = doc(db, 'userCoupons', userId);
    const userCouponSnap = await getDoc(userCouponRef);
    
    if (userCouponSnap.exists()) {
      const userData = userCouponSnap.data();
      if (userData && userData.coupons && userData.coupons[couponId]) {
        const usageCount = userData.coupons[couponId].usageCount || 0;
        if (usageCount >= coupon.usageLimit) {
          return { valid: false, error: 'You have already used this coupon' };
        }
      }
    }

    // Calculate discount
    const discount = calculateDiscount(
      coupon.type,
      coupon.value,
      subtotal,
      0, // deliveryFee will be calculated separately
      { maxDiscount: coupon.maxDiscount }
    );

    return {
      valid: true,
      coupon: {
        id: couponId,
        code: coupon.code,
        type: coupon.type,
        value: coupon.value,
        description: coupon.description,
        minOrderAmount: coupon.minOrderAmount,
        maxDiscount: coupon.maxDiscount,
        category: coupon.category
      },
      discount: discount.discountAmount,
      discountType: discount.discountType,
      finalAmount: discount.finalAmount
    };

  } catch (error) {
    console.error('Error validating coupon:', error);
    return { valid: false, error: 'Error validating coupon' };
  }
}

/**
 * Apply voucher to order
 * @param {string} voucherId - Voucher ID
 * @param {string} userId - User ID
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} - Application result
 */
export async function applyVoucher(voucherId, userId, orderId) {
  try {
    // Update voucher usage count
    const voucherRef = doc(db, 'vouchers', voucherId);
    const voucherSnap = await getDoc(voucherRef);
    const currentUsedCount = voucherSnap.data()?.usedCount || 0;
    
    await updateDoc(voucherRef, {
      usedCount: currentUsedCount + 1,
      lastUsedAt: new Date()
    });

    // Track user usage
    const userVoucherRef = doc(db, 'userVouchers', userId);
    const userVoucherSnap = await getDoc(userVoucherRef);
    
    if (userVoucherSnap.exists() && userVoucherSnap.data()?.vouchers) {
      const userData = userVoucherSnap.data();
      const currentUsageCount = userData.vouchers[voucherId]?.usageCount || 0;
      const currentOrdersCount = userData.vouchers[voucherId]?.orders || 0;
      
      await updateDoc(userVoucherRef, {
        [`vouchers.${voucherId}.usageCount`]: currentUsageCount + 1,
        [`vouchers.${voucherId}.lastUsed`]: new Date(),
        [`vouchers.${voucherId}.orders`]: currentOrdersCount + 1
      });
    } else {
      // Use setDoc with merge: true to either create or update
      await setDoc(userVoucherRef, {
        vouchers: {
          [voucherId]: {
            usageCount: 1,
            lastUsed: new Date(),
            orders: 1
          }
        }
      }, { merge: true });
    }

    return { success: true, message: 'Voucher applied successfully' };

  } catch (error) {
    console.error('Error applying voucher:', error);
    return { success: false, error: 'Error applying voucher' };
  }
}

/**
 * Apply coupon to order
 * @param {string} couponId - Coupon ID
 * @param {string} userId - User ID
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} - Application result
 */
export async function applyCoupon(couponId, userId, orderId) {
  try {
    // Update coupon usage count
    const couponRef = doc(db, 'coupons', couponId);
    const couponSnap = await getDoc(couponRef);
    const currentUsedCount = couponSnap.data()?.usedCount || 0;
    
    await updateDoc(couponRef, {
      usedCount: currentUsedCount + 1,
      lastUsedAt: new Date()
    });

    // Track user usage
    const userCouponRef = doc(db, 'userCoupons', userId);
    const userCouponSnap = await getDoc(userCouponRef);
    
    if (userCouponSnap.exists() && userCouponSnap.data()?.coupons) {
      const userData = userCouponSnap.data();
      const currentUsageCount = userData.coupons[couponId]?.usageCount || 0;
      const currentOrdersCount = userData.coupons[couponId]?.orders || 0;
      
      await updateDoc(userCouponRef, {
        [`coupons.${couponId}.usageCount`]: currentUsageCount + 1,
        [`coupons.${couponId}.lastUsed`]: new Date(),
        [`coupons.${couponId}.orders`]: currentOrdersCount + 1
      });
    } else {
      // Use setDoc with merge: true to either create or update
      await setDoc(userCouponRef, {
        coupons: {
          [couponId]: {
            usageCount: 1,
            lastUsed: new Date(),
            orders: 1
          }
        }
      }, { merge: true });
    }

    return { success: true, message: 'Coupon applied successfully' };

  } catch (error) {
    console.error('Error applying coupon:', error);
    return { success: false, error: 'Error applying coupon' };
  }
}

/**
 * Get available vouchers for user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - List of available vouchers
 */
export async function getAvailableVouchers(userId) {
  try {
    const vouchersQuery = query(
      collection(db, 'vouchers'),
      where('enabled', '==', true)
    );
    
    const vouchersSnap = await getDocs(vouchersQuery);
    const vouchers = [];

    for (const doc of vouchersSnap.docs) {
      const voucher = doc.data();
      const now = new Date();
      const validFrom = voucher.validFrom.toDate();
      const validUntil = voucher.validUntil.toDate();
      
      if (now >= validFrom && now <= validUntil) {
        vouchers.push({
          id: doc.id,
          ...voucher
        });
      }
    }

    return vouchers;

  } catch (error) {
    console.error('Error getting available vouchers:', error);
    return [];
  }
}

/**
 * Get available coupons for user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - List of available coupons
 */
export async function getAvailableCoupons(userId) {
  try {
    const couponsQuery = query(
      collection(db, 'coupons'),
      where('enabled', '==', true)
    );
    
    const couponsSnap = await getDocs(couponsQuery);
    const coupons = [];

    for (const doc of couponsSnap.docs) {
      const coupon = doc.data();
      const now = new Date();
      const validFrom = coupon.validFrom.toDate();
      const validUntil = coupon.validUntil.toDate();
      
      if (now >= validFrom && now <= validUntil) {
        coupons.push({
          id: doc.id,
          ...coupon
        });
      }
    }

    return coupons;

  } catch (error) {
    console.error('Error getting available coupons:', error);
    return [];
  }
}

/**
 * Calculate overweight delivery fees
 * @param {number} orderWeight - Order weight in kg
 * @param {Object} overweightSettings - Overweight fee settings
 * @returns {Object} - { overweightFee, breakdown }
 */
export function calculateOverweightFee(orderWeight, overweightSettings) {
  if (!overweightSettings.enabled || orderWeight <= overweightSettings.threshold) {
    return {
      overweightFee: 0,
      breakdown: {
        weightAboveThreshold: 0,
        increments: 0,
        fixedFee: 0,
        perKgFee: 0
      }
    };
  }

  const weightAboveThreshold = orderWeight - overweightSettings.threshold;
  const increments = Math.ceil(weightAboveThreshold / overweightSettings.increment);
  const fixedFee = increments * overweightSettings.additionalFee;
  const perKgFee = weightAboveThreshold * overweightSettings.perKgFee;
  const totalOverweightFee = fixedFee + perKgFee;

  return {
    overweightFee: Math.round(totalOverweightFee * 100) / 100,
    breakdown: {
      weightAboveThreshold: Math.round(weightAboveThreshold * 100) / 100,
      increments,
      fixedFee: Math.round(fixedFee * 100) / 100,
      perKgFee: Math.round(perKgFee * 100) / 100
    }
  };
}
